/**
 * Authenticated report download helper (Order 31G).
 */

import type { ReportExportFormat } from "@multi-chat/shared";
import { API_BASE_URL, ApiError, isApiError } from "../../api/client.js";

function filenameFromContentDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const utf = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf?.[1]) {
    try {
      return decodeURIComponent(utf[1].trim());
    } catch {
      // fall through
    }
  }
  const plain = /filename="([^"]+)"/i.exec(header) ?? /filename=([^;]+)/i.exec(header);
  if (plain?.[1]) return plain[1].trim();
  return fallback;
}

export async function downloadReportExport(input: {
  pathWithQuery: string;
  fallbackFilename: string;
  signal?: AbortSignal;
}): Promise<void> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${input.pathWithQuery}`, {
      method: "GET",
      credentials: "include",
      signal: input.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new ApiError(
      error instanceof Error && error.message
        ? `Cannot reach the server: ${error.message}`
        : "Cannot reach the server",
      { status: 0 },
    );
  }

  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // ignore
    }
    const message =
      body &&
      typeof body === "object" &&
      "error" in body &&
      body.error &&
      typeof body.error === "object" &&
      "message" in body.error &&
      typeof (body.error as { message: unknown }).message === "string"
        ? (body.error as { message: string }).message
        : `Export failed with status ${response.status}`;
    throw new ApiError(message, { status: response.status });
  }

  const blob = await response.blob();
  const filename = filenameFromContentDisposition(
    response.headers.get("Content-Disposition"),
    input.fallbackFilename,
  );
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = "noopener";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function reportExportErrorMessage(error: unknown, fallback: string): string {
  if (isApiError(error)) {
    if (error.status === 403) return "forbidden";
    if (error.status === 0) return "unavailable";
  }
  return fallback;
}

export type { ReportExportFormat };
